import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, Eye, Settings, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { db } from '../../../lib/db';
import { useTemplates } from '../../../hooks/useDatabase';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { v4 as uuidv4 } from 'uuid';
import { aiService } from '../../../lib/aiService';
import { useConfirmDialog } from '../../shared/ConfirmDialog';

export default function TemplateBuilder() {
  const { user } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const { createTemplate, updateTemplate, loading } = useTemplates();
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    const data = await db.templates.toArray();
    // Deduplicate by name+type combo to hide double-seeded templates
    const seen = new Set();
    const cleaned = data.filter(t => {
      const key = `${t.name?.toLowerCase() || ''}-${t.type || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setTemplates(cleaned);
  };

  const handleCreateNew = () => {
    setEditingTemplate({
      id: null,
      name: '',
      description: '',
      type: 'annual',
      sections: [{ id: uuidv4(), name: 'Performance Areas', order: 1, competencies: [] }],
      settings: { weightingEnabled: true, notifications: { enabled: true, daysBefore: [30, 14, 7] } }
    });
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setShowCreateModal(true);
  };

  const handleAddCompetency = () => {
    if (!editingTemplate) return;
    const newCompetency = {
      id: uuidv4(),
      name: '',
      description: '',
      weight: 20,
      scale: { type: 'numeric', min: 0, max: 5 },
      feedbackThreshold: 1,
      allowManualInput: true
    };
    const updated = { ...editingTemplate };
    updated.sections[0].competencies = [...(updated.sections[0].competencies || []), newCompetency];
    setEditingTemplate(updated);
  };

  const handleCompetencyChange = (index, field, value) => {
    const updated = { ...editingTemplate };
    updated.sections[0].competencies[index][field] = value;
    setEditingTemplate(updated);
  };

  const handleRemoveCompetency = (index) => {
    const updated = { ...editingTemplate };
    updated.sections[0].competencies.splice(index, 1);
    setEditingTemplate(updated);
  };

  const getTotalWeight = () => {
    if (!editingTemplate?.sections[0]?.competencies) return 0;
    return editingTemplate.sections[0].competencies.reduce((sum, c) => sum + (c.weight || 0), 0);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name) {
      toast.error('Please enter a template name');
      return;
    }
    if (getTotalWeight() !== 100 && editingTemplate.sections[0]?.competencies?.length > 0) {
      toast.warning('Total weight should equal 100%');
    }

    try {
      if (editingTemplate.id) {
        await updateTemplate(editingTemplate.id, editingTemplate);
        toast.success('Template updated successfully!');
      } else {
        await createTemplate({ ...editingTemplate, createdBy: user.id });
        toast.success('Template created successfully!');
      }
      setShowCreateModal(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      description: 'Are you sure you want to delete this template?',
      confirmText: 'Delete',
      variant: 'destructive'
    });
    if (confirmed) {
      await db.templates.delete(id);
      toast.success('Template deleted');
      loadTemplates();
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    
    setIsGenerating(true);
    
    try {
      const generatedTemplate = await aiService.generateTemplate(aiPrompt);
      
      // Ensure the template has a null ID so it's treated as new
      const newTemplate = {
        ...generatedTemplate,
        id: null,
      };

      setEditingTemplate(newTemplate);
      setShowCreateModal(true);
      setShowAiModal(false);
      setAiPrompt('');
      toast.success('Template generated by AI!');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to generate template. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Template Builder</h1>
          <p className="text-gray-500 mt-1">Create and manage appraisal templates</p>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => setShowAiModal(true)} 
            className="bg-[linear-gradient(45deg,#9a2c97,#2f4498)] rounded-[50px] text-white font-semibold uppercase tracking-widest text-xs px-6 py-2.5 hover:opacity-90 transition-opacity shadow-md"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Create with AI
          </Button>
          <Button onClick={handleCreateNew} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(template => (
          <Card key={template.id} className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={() => handleEditTemplate(template)}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                </div>
                <Badge className={`${template.type === 'annual' ? 'bg-teal-100 text-teal-700' : template.type === 'introductory' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                  {template.type}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{template.sections[0]?.competencies?.length || 0} competencies</span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditTemplate(template); }}><Settings className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }} className="text-rose-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No templates yet. Create your first template!</p>
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.id ? 'Edit Template' : 'Create New Template'}</DialogTitle>
            <DialogDescription>Define the structure and competencies for your appraisal template</DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input value={editingTemplate.name} onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })} placeholder="Annual Performance Review" />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={editingTemplate.type} onValueChange={(v) => setEditingTemplate({ ...editingTemplate, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="introductory">Introductory</SelectItem>
                      <SelectItem value="probation">Probation</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editingTemplate.description} onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })} placeholder="Describe the purpose of this template..." />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Competencies</Label>
                    <p className={`text-sm ${getTotalWeight() === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>Total weight: {getTotalWeight()}% {getTotalWeight() !== 100 && '(should be 100%)'}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleAddCompetency}><Plus className="w-4 h-4 mr-1" /> Add Competency</Button>
                </div>

                {editingTemplate.sections[0]?.competencies?.map((comp, index) => (
                  <Card key={comp.id} className="border border-gray-200">
                    <CardContent className="pt-4 space-y-4">
                      <div className="flex items-start gap-4">
                        <GripVertical className="w-5 h-5 text-gray-400 mt-2 cursor-grab" />
                        <div className="flex-1 grid grid-cols-3 gap-4">
                          <div className="col-span-2 space-y-2">
                            <Label>Name</Label>
                            <Input value={comp.name} onChange={(e) => handleCompetencyChange(index, 'name', e.target.value)} placeholder="Communication" />
                          </div>
                          <div className="space-y-2">
                            <Label>Weight (%)</Label>
                            <Input type="number" value={comp.weight} onChange={(e) => handleCompetencyChange(index, 'weight', parseInt(e.target.value) || 0)} min={0} max={100} />
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="text-gray-400 hover:text-rose-500" onClick={() => handleRemoveCompetency(index)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      <div className="pl-9">
                        <Label>Description</Label>
                        <Textarea value={comp.description} onChange={(e) => handleCompetencyChange(index, 'description', e.target.value)} placeholder="Describe this competency..." className="mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {(!editingTemplate.sections[0]?.competencies || editingTemplate.sections[0].competencies.length === 0) && (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    <p className="text-gray-500">No competencies added yet</p>
                    <Button variant="link" onClick={handleAddCompetency} className="text-teal-600">Add your first competency</Button>
                  </div>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100">
                <Label className="text-base">Settings</Label>
                <div className="flex items-center justify-between">
                  <div><p className="font-medium text-gray-900">Enable Weighting</p><p className="text-sm text-gray-500">Use weighted scoring for competencies</p></div>
                  <Switch checked={editingTemplate.settings?.weightingEnabled} onCheckedChange={(v) => setEditingTemplate({...editingTemplate, settings: { ...editingTemplate.settings, weightingEnabled: v }})} />
                </div>
                <div className="flex items-center justify-between">
                  <div><p className="font-medium text-gray-900">Send Notifications</p><p className="text-sm text-gray-500">Remind employees before due dates</p></div>
                  <Switch checked={editingTemplate.settings?.notifications?.enabled} onCheckedChange={(v) => setEditingTemplate({...editingTemplate, settings: { ...editingTemplate.settings, notifications: { ...editingTemplate.settings?.notifications, enabled: v }}})} />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">
              <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAiModal} onOpenChange={setShowAiModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Create Template with AI
            </DialogTitle>
            <DialogDescription>
              Describe the role or purpose, and let AI build a template for you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description / Prompt</Label>
              <Textarea 
                placeholder="e.g. Sales Executive annual review with focus on targets and client relationships..." 
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAiGenerate} 
              disabled={!aiPrompt.trim() || isGenerating}
              className="bg-[linear-gradient(45deg,#9a2c97,#2f4498)] text-white border-0 hover:opacity-90"
            >
              {isGenerating ? 'Generating...' : 'Generate Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
